import {
  Button,
  Icon,
  LoaderSpinner,
  TabContent,
} from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { ProductModal, InventoryProvider } from '@vend/business-components'
import { detectCreditCard } from '@vend/credit-card-detector'
import { trackUserEvent } from '@vend/utilities'
import { debounce } from 'lodash'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { vn } from 'vend-number'
import { FEATURE_SHOW_SPECIAL_ORDERS_IN_INVENTORY } from '../../../constants'
import { isGiftCard } from '../../../functions/lineItem'
import { FulfilmentType } from '../../../models/fulfilment'
import {
  isLineItemNoteTooLong,
  LINE_ITEM_NOTE_LENGTH,
  LineItem,
  LineItemUser,
} from '../../../models/line-item'
import {
  focusGlobalSearch,
  useSaleManager,
  useVdTracking,
} from '../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  selectSaleId,
  selectSaleLocalId,
  updateLineItem,
} from '../../../state/slices/currentSale/currentSaleSlice'
import {
  selectLoggedInUser,
  selectLoggedInUserId,
} from '../../../state/slices/posState/posStateSlice'
import {
  selectExpandedLineItemId,
  selectLineItemCurrentTab,
  selectLineItemDiscountError,
  selectLineItemPriceInputError,
  selectLineItemQuantityInputError,
  setLineItemCurrentTab,
  setLineItemDiscountInputError,
  setLineItemNoteError,
  setLineItemPriceInputError,
  setLineItemQuantityInputError,
} from '../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { NoteInput } from '../../shared/NoteInput'
import { WEB_REGISTER } from '../../web-register.constant'
import { SalesPersonInfo } from '../sales-person-popover/SalesPersonInfo'
import { SalesPersonSelect } from '../sales-person-popover/SalesPersonSelect'
import { LineItemTab } from '../../../../types/lineItemTabs'
import { LineItemDiscountPercent } from './LineItemDiscountPercent'
import { LineItemMarkupPercent } from './LineItemMarkupPercent'
import { LineItemPrice } from './LineItemPrice'
import { LineItemQuantity } from './LineItemQuantity'
import { LineItemTabs } from './LineItemTabs'
import { LineItemDisplayFulfilment } from './fulfilment/LineItemDisplayFulfilment'
import { LineItemDiscountError } from './input-error-message/LineItemDiscountError'
import { LineItemPriceError } from './input-error-message/LineItemPriceError'
import { LineItemQuantityError } from './input-error-message/LineItemQuantityError'
import { NoReturnPermissionError } from './input-error-message/NoReturnPermissionError'
import { LineItemSplitQuantityButton } from './split-quantity/LineItemSplitQuantityButton'

interface Props {
  lineItem: LineItem
  editAllowedForConfirmed?: boolean
  elementToFocus?: 'quantity' | 'price'
  onSelectFulfilmentType?: (
    type: FulfilmentType,
    fulfilmentOutletId?: string
  ) => void
}

/** This is based on $vd-time-s in css variable from helios css, we want to wait for the transition to finish before we focus on the input element otherwise it won't work */
const TRANSITION_DELAY = 200 // ms

export const LineItemExpanded: FunctionComponent<Props> = ({
  lineItem,
  editAllowedForConfirmed,
  elementToFocus = 'quantity',
  onSelectFulfilmentType = (
    type: FulfilmentType,
    fulfilmentOutletId?: string
  ) => {},
}) => {
  const t = useT()
  const dispatch = useAppDispatch()
  const quantityError = useAppSelector(
    selectLineItemQuantityInputError(lineItem.localId)
  )
  const priceError = useAppSelector(
    selectLineItemPriceInputError(lineItem.localId)
  )
  const discountError = useAppSelector(
    selectLineItemDiscountError(lineItem.localId)
  )
  const saleManager = useSaleManager()
  const saleId = useAppSelector(selectSaleId)
  const saleLocalId = useAppSelector(selectSaleLocalId)

  const showSalePerson = getFeatureFromCache('sales_person_attribution')
  const saleUser = useAppSelector(selectLoggedInUser)
  const vdTracking = useVdTracking()

  const loggedInUserID = useAppSelector(selectLoggedInUserId)
  const [hasUserSwitched, setHasUserSwitched] = useState<boolean>(false)

  const debouncedSaveSale = useRef(debounce(() => saleManager.saveSale(), 300))
    .current
  const saveSale = useCallback(() => {
    debouncedSaveSale()
  }, [debouncedSaveSale])

  const showSpecialOrderColumn = getFeatureFromCache(
    FEATURE_SHOW_SPECIAL_ORDERS_IN_INVENTORY
  )

  const inventoryConfig = showSpecialOrderColumn
    ? { specialOrderColumn: { position: 'early' as const, showBorder: false } }
    : {}

  const showSplitQuantityButton = !lineItem.giftCardNumber

  const isSplitQuantityButtonDisabled =
    Number(lineItem.quantity) === 0 ||
    lineItem.confirmed ||
    quantityError !== null ||
    priceError !== null ||
    discountError !== null

  useEffect(() => {
    // Set to true for one render cycle
    setHasUserSwitched(true)
  }, [loggedInUserID])

  useEffect(() => {
    if (hasUserSwitched) {
      setHasUserSwitched(false)
    }
  }, [hasUserSwitched])

  const [showProductModal, setShowProductModal] = useState(false)
  const expandedLineItem = useAppSelector(selectExpandedLineItemId)
  const quantityInputRef = useRef<HTMLInputElement>(null)
  const priceInputRef = useRef<HTMLInputElement>(null)
  const onQuantityError = useCallback(
    (error: string | null) => {
      dispatch(
        setLineItemQuantityInputError({
          id: lineItem.localId,
          error,
        })
      )
    },
    [dispatch, lineItem.localId]
  )

  const onPriceError = useCallback(
    (error: string | null) => {
      dispatch(
        setLineItemPriceInputError({
          id: lineItem.localId,
          error,
        })
      )
    },
    [dispatch, lineItem.localId]
  )

  const onDiscountError = useCallback(
    (error: string | null) => {
      dispatch(
        setLineItemDiscountInputError({
          id: lineItem.localId,
          error,
        })
      )
    },
    [dispatch, lineItem.localId]
  )

  const fulfillmentTab = useAppSelector(selectLineItemCurrentTab)
  const onFulfillmentTabChange = (tab: LineItemTab) => {
    dispatch(setLineItemCurrentTab(tab))
  }

  const isPriceMarkedUp = vn(lineItem.displayMarkupPercent).gt(0)
  useEffect(() => {
    if (expandedLineItem !== lineItem.localId) {
      return
    }

    if (elementToFocus === 'price') {
      setTimeout(() => {
        priceInputRef.current?.focus()
        priceInputRef.current?.setSelectionRange(
          0,
          priceInputRef.current.value.length
        )
      }, TRANSITION_DELAY)

      return
    }

    setTimeout(() => {
      quantityInputRef.current?.focus()
      quantityInputRef.current?.setSelectionRange(
        0,
        quantityInputRef.current.value.length
      )
    }, TRANSITION_DELAY)
  }, [expandedLineItem, lineItem.localId, elementToFocus])

  if (lineItem.calculating) {
    return (
      <div className="li-cell-actions-container-section">
        <div className="wr-media wr-media--center li-cell-actions--calculating">
          <LoaderSpinner small className="wr-media-object" />
          <div className="wr-media-body vd-text--secondary">
            {t('Loading product information')}
          </div>
        </div>
      </div>
    )
  }

  const onNoteChange = (note: string = '') => {
    dispatch(updateLineItem({ ...lineItem, note }))

    const noteTooLongError = isLineItemNoteTooLong(note) ? 'noteTooLong' : null
    const creditCardError = detectCreditCard(note) ? 'hasCreditCard' : null
    dispatch(
      setLineItemNoteError({
        id: lineItem.localId,
        error: noteTooLongError || creditCardError,
      })
    )

    saveSale()
  }

  const openProductModal = () => {
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      'Show Product Information',
      {
        view: 'Line Item',
      },
      { useSendBeacon: true }
    )
    setShowProductModal(true)
  }

  const onSalesPersonSelect = (user: LineItemUser) => {
    vdTracking.trackUserEvent({
      eventName: 'sale.line_item.salesperson.update',
      metadata: {
        saleId: saleId || saleLocalId,
        userId: user.id,
        lineItemId: lineItem.id || lineItem.localId,
      },
    })

    dispatch(updateLineItem({ ...lineItem, salesperson: user }))
    saleManager.saveSale()
  }

  const readonly = isGiftCard(lineItem.product)

  return (
    <LineItemTabs
      defaultTab={fulfillmentTab}
      onChange={onFulfillmentTabChange}
      fulfillmentType={lineItem.fulfilmentType}
      lineItemId={lineItem.id || lineItem.localId}
    >
      <TabContent name={LineItemTab.DETAIL}>
        <div className="li-cell-actions-container-section">
          <div className="vd-flex">
            <div className="li-cell-actions">
              <LineItemQuantity
                lineItem={lineItem}
                editAllowedForConfirmed={editAllowedForConfirmed}
                onError={onQuantityError}
                ref={quantityInputRef}
                readonly={readonly}
              />
              <LineItemQuantityError lineItem={lineItem} />
            </div>
            <div className="li-cell-actions">
              <LineItemPrice
                lineItem={lineItem}
                editAllowedForConfirmed={editAllowedForConfirmed}
                onError={onPriceError}
                error={priceError}
                hasUserSwitched={() => hasUserSwitched}
                ref={priceInputRef}
                readonly={readonly}
              />
              <LineItemPriceError lineItemID={lineItem.localId} />
            </div>
            {isPriceMarkedUp ? (
              <div className="li-cell-actions pro-line-item-markup vd-util-text-overflow-break-word">
                <LineItemMarkupPercent lineItem={lineItem} />
              </div>
            ) : (
              <div className="li-cell-actions pro-line-item-discount">
                <LineItemDiscountPercent
                  lineItem={lineItem}
                  editAllowedForConfirmed={editAllowedForConfirmed}
                  onError={onDiscountError}
                  error={discountError}
                  hasUserSwitched={() => hasUserSwitched}
                  readonly={readonly}
                />
                <LineItemDiscountError lineItemID={lineItem.localId} />
              </div>
            )}
          </div>

          <NoReturnPermissionError lineItem={lineItem} />

          <div className="vd-pb1 vd-pt1">
            <div className="vd-field">
              {(editAllowedForConfirmed ||
                !lineItem.confirmed ||
                lineItem.note) && <div className="vd-label">{t('Note')}</div>}
              <div className="vd-value">
                {lineItem.confirmed && !editAllowedForConfirmed ? (
                  lineItem.note && <div>{lineItem.note}</div>
                ) : (
                  <NoteInput
                    placeholder={t('Enter a note', {
                      _context: 'sale note placeholder',
                    })}
                    note={lineItem.note}
                    rows={1}
                    name="lineItemNote"
                    charLimit={LINE_ITEM_NOTE_LENGTH}
                    onChange={
                      onNoteChange &&
                      (event => onNoteChange(event.target.value))
                    }
                    onBlur={saveSale}
                  />
                )}
              </div>
            </div>
          </div>
          {showSplitQuantityButton && (
            <div>
              <LineItemSplitQuantityButton
                lineItem={lineItem}
                disabled={isSplitQuantityButtonDisabled}
              />
            </div>
          )}
          <div className="li-cell-actions-container-info">
            {!lineItem.confirmed && showSalePerson && saleUser ? (
              <div className="vd-text-supplementary li-cell-actions-sold-by">
                <SalesPersonSelect
                  selectedUser={lineItem.salesperson}
                  onSelect={onSalesPersonSelect}
                  loggedInUser={saleUser}
                />
              </div>
            ) : null}
            {lineItem.confirmed && showSalePerson && saleUser ? (
              <SalesPersonInfo name={saleUser.display_name} />
            ) : null}
            <Button
              variant="supplementary"
              modifier="text"
              onClick={openProductModal}
              data-track="show-inventory-and-details"
              className="vd-flex vd-flex--align-center vd-flex--justify-left"
            >
              <Icon icon="fa fa-info-circle vd-text-supplementary vd-pr1" />
              {t('Show Inventory & Details')}
            </Button>
          </div>
        </div>
      </TabContent>

      <TabContent name={LineItemTab.FULFILMENT}>
        <div className="sale-fulfilment vd-pb4">
          <LineItemDisplayFulfilment
            lineItem={lineItem}
            onSelectFulfilmentType={onSelectFulfilmentType}
          />
        </div>
      </TabContent>
      {showProductModal ? (
        <InventoryProvider config={inventoryConfig}>
          <ProductModal
            productId={
              lineItem.product.variant_parent_id || lineItem.product.id
            }
            onClose={() => {
              setShowProductModal(false)
              focusGlobalSearch()
            }}
          />
        </InventoryProvider>
      ) : null}
    </LineItemTabs>
  )
}
